/**
 * React Query hooks for workspace-scoped tag management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagService, type TagDefinition } from '../services/tagService';

export type { TagDefinition } from '../services/tagService';

export const tagKeys = {
    all: ['tags'] as const,
    list: () => [...tagKeys.all, 'list'] as const,
};

/**
 * Hook to list all workspace tags.
 */
export function useTags() {
    return useQuery({
        queryKey: tagKeys.list(),
        queryFn: () => tagService.listTags(),
    });
}

/**
 * Hook to create a new workspace tag.
 */
export function useCreateTag() {
    const queryClient = useQueryClient();
    return useMutation<TagDefinition, Error, { name: string; color: string }>({
        mutationFn: ({ name, color }) => tagService.createTag(name, color),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.list() });
        },
    });
}

/**
 * Hook to update an existing workspace tag.
 */
export function useUpdateTag() {
    const queryClient = useQueryClient();
    return useMutation<TagDefinition, Error, { tagId: string; name: string; color: string }>({
        mutationFn: ({ tagId, name, color }) => tagService.updateTag(tagId, name, color),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.list() });
        },
    });
}

/**
 * Hook to delete a workspace tag (also removes from all documents).
 */
export function useDeleteTag() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: (tagId) => tagService.deleteTag(tagId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.list() });
            // Also invalidate documents since tag assignments are removed
            queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });
}

/**
 * Hook to update tags assigned to a document.
 */
export function useUpdateDocumentTags() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { docId: string; tagIds: string[] }>({
        mutationFn: ({ docId, tagIds }) => tagService.updateDocumentTags(docId, tagIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });
}
